import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  BookOpen, ArrowLeft, ArrowRight, Users, Clock, Layers,
  CheckCircle, Search, RefreshCw, AlertCircle, Trash2,
  ChevronDown, ChevronRight, Plus, Save, FileSpreadsheet, Mail
} from 'lucide-react';
import './PopLMSSection.css';
import '../pages/FacultyDashboard.css';
import AttendanceExportControls from './AttendanceExportControls';

// ─────────────────────────────────────────────────────────────────────────────
// Attendance helpers (localStorage-backed, synced to server)
// ─────────────────────────────────────────────────────────────────────────────
const getAttendanceSessionId = (date, time) => `${date}_${time}`;
const getAttendanceStorageKey = (assignId, sessionId) => `pop_att_${assignId}_${sessionId}`;
const getAttendanceSessionsKey = (assignId) => `pop_att_sessions_${assignId}`;

const saveAttendanceSessions = (assignId, sessions) => {
  localStorage.setItem(getAttendanceSessionsKey(assignId), JSON.stringify(sessions));
};

const loadAttendanceSessions = (assignId) => {
  try { return JSON.parse(localStorage.getItem(getAttendanceSessionsKey(assignId)) || '[]'); }
  catch { return []; }
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const d = new Date(); d.setHours(h || 0, m || 0, 0, 0);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const PopLMSSection = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Detail view
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [detailSection, setDetailSection] = useState('attendance'); // 'attendance' | 'testMarks'

  // Attendance state
  const [attendanceDate, setAttendanceDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [attendanceTime, setAttendanceTime] = useState(() => new Date().toTimeString().slice(0, 5));
  const [attendanceSessions, setAttendanceSessions] = useState({});
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [attendanceDraftRecords, setAttendanceDraftRecords] = useState({});
  const [attendanceSaveStatus, setAttendanceSaveStatus] = useState('');

  // Test marks state
  const [testMarksList, setTestMarksList] = useState({});        // { assignId: [{ test_name, max_marks }] }
  const [testMarksRecords, setTestMarksRecords] = useState({});  // { `${assignId}-${test}`: { studentId: marks } }
  const [testMarksDraft, setTestMarksDraft] = useState({});
  const [testMarksSaveStatus, setTestMarksSaveStatus] = useState('');
  const [newTestName, setNewTestName] = useState('');
  const [newMaxMarks, setNewMaxMarks] = useState(100);

  // Full description modal
  const [fullDescCourse, setFullDescCourse] = useState(null);

  // ── Fetch courses ──────────────────────────────────────────────────────────
  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/integrations/edvergencex/pop-courses');
      setAssignments(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Failed to fetch PoP courses:', err);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  // ── Load attendance & test marks when assignment is selected ───────────────
  useEffect(() => {
    if (!selectedAssignment) return;
    const id = selectedAssignment.id;

    // Load attendance from server
    api.get(`/faculty/lms/assigned/${id}/attendance`).then(res => {
      // Backend returns { records: { studentId: status, ... } } per date/time — 
      // or may return array. Handle gracefully.
      const data = res.data;
      const records = Array.isArray(data) ? data : (Array.isArray(data?.records) ? data.records : []);
      const sessionMap = new Map();
      const nextRecords = {};
      records.forEach(rec => {
        const sid = getAttendanceSessionId(rec.class_date, rec.class_time);
        sessionMap.set(sid, { id: sid, date: rec.class_date, time: rec.class_time });
        const key = `${id}-${sid}`;
        nextRecords[key] = { ...(nextRecords[key] || {}), [rec.student_id]: rec.status || 'present' };
      });
      const sessions = Array.from(sessionMap.values()).sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
      saveAttendanceSessions(id, sessions);
      setAttendanceSessions(prev => ({ ...prev, [id]: sessions }));
      setAttendanceRecords(prev => ({ ...prev, ...nextRecords }));
    }).catch(() => {});

    // Load test marks from server
    api.get(`/faculty/lms/assigned/${id}/test_marks`).then(res => {
      const data = res.data;
      const marks = Array.isArray(data) ? data : (Array.isArray(data?.records) ? data.records : []);
      const testsSet = new Set();
      const testsInfo = [];
      const nextRecords = {};
      marks.forEach(m => {
        const key = `${id}-${m.test_name}`;
        if (!testsSet.has(m.test_name)) {
          testsSet.add(m.test_name);
          testsInfo.push({ test_name: m.test_name, max_marks: m.max_marks });
        }
        nextRecords[key] = { ...(nextRecords[key] || {}), [m.student_id]: m.marks_obtained };
      });
      setTestMarksList(prev => ({ ...prev, [id]: testsInfo }));
      setTestMarksRecords(prev => ({ ...prev, ...nextRecords }));
    }).catch(() => {});
  }, [selectedAssignment?.id]);

  // ── Attendance helpers ─────────────────────────────────────────────────────
  const getSessions = (id) => attendanceSessions[id] || loadAttendanceSessions(id);

  const getStudents = (assignment) => assignment?.students || [];

  const getAttendanceStatus = (assignId, sessionId, studentId) => {
    const key = `${assignId}-${sessionId}`;
    return attendanceDraftRecords[key]?.[studentId] ?? attendanceRecords[key]?.[studentId] ?? 'present';
  };

  const addSession = () => {
    const id = selectedAssignment.id;
    if (!attendanceDate || !attendanceTime) return;
    const sessionId = getAttendanceSessionId(attendanceDate, attendanceTime);
    const current = getSessions(id);
    if (current.some(s => s.id === sessionId)) return;
    const next = [...current, { id: sessionId, date: attendanceDate, time: attendanceTime }]
      .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
    saveAttendanceSessions(id, next);
    setAttendanceSessions(prev => ({ ...prev, [id]: next }));
    // default all present
    const students = getStudents(selectedAssignment);
    const defaults = Object.fromEntries(students.map(s => [s.id, 'present']));
    localStorage.setItem(getAttendanceStorageKey(id, sessionId), JSON.stringify(defaults));
    setAttendanceRecords(prev => ({ ...prev, [`${id}-${sessionId}`]: defaults }));
    setAttendanceSaveStatus('');
  };

  const removeSession = async (sessionId) => {
    const id = selectedAssignment.id;
    const session = getSessions(id).find(s => s.id === sessionId);
    const next = getSessions(id).filter(s => s.id !== sessionId);
    saveAttendanceSessions(id, next);
    localStorage.removeItem(getAttendanceStorageKey(id, sessionId));
    setAttendanceSessions(prev => ({ ...prev, [id]: next }));
    setAttendanceRecords(prev => { const n = { ...prev }; delete n[`${id}-${sessionId}`]; return n; });
    setAttendanceDraftRecords(prev => { const n = { ...prev }; delete n[`${id}-${sessionId}`]; return n; });
    if (session) {
      try { await api.delete(`/faculty/lms/assigned/${id}/attendance`, { params: { class_date: session.date, class_time: session.time } }); }
      catch (err) { console.error(err); }
    }
  };

  const setStatus = (assignId, sessionId, studentId, status) => {
    const key = `${assignId}-${sessionId}`;
    setAttendanceDraftRecords(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [studentId]: status } }));
    setAttendanceSaveStatus('');
  };

  const saveVisibleAttendance = async () => {
    const id = selectedAssignment.id;
    const students = getStudents(selectedAssignment);
    const sessions = getSessions(id);
    const records = [];

    sessions.forEach(session => {
      const key = `${id}-${session.id}`;
      const draft = { ...(attendanceRecords[key] || {}), ...(attendanceDraftRecords[key] || {}) };
      students.forEach(s => {
        records.push({
          student_id: s.id,
          class_date: session.date,
          class_time: session.time,
          status: draft[s.id] || 'present'
        });
      });
    });

    setAttendanceSaveStatus('saving');
    try {
      await api.post(`/faculty/lms/assigned/${id}/attendance`, { records });
      sessions.forEach(session => {
        const key = `${id}-${session.id}`;
        const draft = { ...(attendanceRecords[key] || {}), ...(attendanceDraftRecords[key] || {}) };
        setAttendanceRecords(prev => ({ ...prev, [key]: draft }));
        setAttendanceDraftRecords(prev => { const n = { ...prev }; delete n[key]; return n; });
      });
      setAttendanceSaveStatus('saved');
      setTimeout(() => setAttendanceSaveStatus(''), 2500);
    } catch (err) {
      setAttendanceSaveStatus('error');
      console.error(err);
    }
  };

  // ── Test marks helpers ─────────────────────────────────────────────────────
  const getTests = (id) => testMarksList[id] || [];

  const getMarksDraft = (assignId, testName, studentId) => {
    const key = `${assignId}-${testName}`;
    return testMarksDraft[key]?.[studentId] ?? testMarksRecords[key]?.[studentId] ?? '';
  };

  const setMarksDraft = (assignId, testName, studentId, val) => {
    const key = `${assignId}-${testName}`;
    setTestMarksDraft(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [studentId]: val } }));
    setTestMarksSaveStatus('');
  };

  const addTest = () => {
    if (!newTestName.trim()) return;
    const id = selectedAssignment.id;
    const existing = getTests(id);
    if (existing.some(t => t.test_name === newTestName.trim())) return;
    setTestMarksList(prev => ({ ...prev, [id]: [...existing, { test_name: newTestName.trim(), max_marks: Number(newMaxMarks) || 100 }] }));
    setNewTestName('');
    setNewMaxMarks(100);
  };

  const removeTest = async (testName) => {
    const id = selectedAssignment.id;
    const next = getTests(id).filter(t => t.test_name !== testName);
    setTestMarksList(prev => ({ ...prev, [id]: next }));
    const key = `${id}-${testName}`;
    setTestMarksRecords(prev => { const n = { ...prev }; delete n[key]; return n; });
    setTestMarksDraft(prev => { const n = { ...prev }; delete n[key]; return n; });
    try {
      await api.delete(`/faculty/lms/assigned/${id}/test_marks`, { params: { test_name: testName } });
    } catch (err) { console.error(err); }
  };

  const saveVisibleTestMarks = async () => {
    const id = selectedAssignment.id;
    const students = getStudents(selectedAssignment);
    const tests = getTests(id);
    const records = [];

    tests.forEach(test => {
      const key = `${id}-${test.test_name}`;
      const draft = { ...(testMarksRecords[key] || {}), ...(testMarksDraft[key] || {}) };
      students.forEach(s => {
        records.push({
          student_id: s.id,
          test_name: test.test_name,
          max_marks: test.max_marks || 100,
          marks_obtained: Number(draft[s.id]) || 0
        });
      });
    });

    setTestMarksSaveStatus('saving');
    try {
      await api.post(`/faculty/lms/assigned/${id}/test_marks`, { records });
      tests.forEach(test => {
        const key = `${id}-${test.test_name}`;
        const draft = { ...(testMarksRecords[key] || {}), ...(testMarksDraft[key] || {}) };
        setTestMarksRecords(prev => ({ ...prev, [key]: draft }));
        setTestMarksDraft(prev => { const n = { ...prev }; delete n[key]; return n; });
      });
      setTestMarksSaveStatus('saved');
      setTimeout(() => setTestMarksSaveStatus(''), 2500);
    } catch (err) {
      setTestMarksSaveStatus('error');
      console.error(err);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getLessonCount = (course) => {
    if (!Array.isArray(course?.modules_data)) return 0;
    return course.modules_data.reduce((t, m) => t + (m.lessons?.length || 0), 0);
  };

  const filtered = assignments.filter(a =>
    (a.course?.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (a.course?.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ─────────────────────────────────────────────────────────────────────────
  // DETAIL VIEW (matches Faculty renderCourseDetail)
  // ─────────────────────────────────────────────────────────────────────────
  if (selectedAssignment) {
    const { id, batch, university_name } = selectedAssignment;
    const course = selectedAssignment.course || {};
    const students = getStudents(selectedAssignment);
    const sessions = getSessions(id);

    const tests = getTests(id);

    return (
      <div className="faculty-course-detail faculty-dashboard">
        <button className="faculty-back-btn" onClick={() => setSelectedAssignment(null)}>
          <ArrowLeft size={16} /> Back to My Courses
        </button>

        <div className="faculty-detail-hero">
          <div className="faculty-card-kicker">
            <span>{course.category || 'Curriculum'}</span>
            <span>{batch || 'All Batches'}</span>
          </div>
          <h2>{course.title || 'Assigned Course'}</h2>
          <div className="faculty-course-meta">
            <span><Users size={15} /> {students.length} Students</span>
            {university_name && <span>· {university_name}</span>}
          </div>
        </div>

        <div className="faculty-detail-tabs">
          <button type="button" className={detailSection === 'attendance' ? 'active' : ''} onClick={() => setDetailSection('attendance')}>
            Student Attendance
          </button>
          <button type="button" className={detailSection === 'testMarks' ? 'active' : ''} onClick={() => setDetailSection('testMarks')}>
            Test Marks
          </button>
        </div>

        {/* ── ATTENDANCE TAB ── */}
        {detailSection === 'attendance' && (
          <div className="faculty-panel faculty-attendance-panel">
            <div className="faculty-attendance-head">
              <div>
                <h3>Student Attendance</h3>
                <p className="faculty-muted">{batch || 'All Batches'} · {students.length} students</p>
              </div>
              <label>
                Class Date
                <input type="date" value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)} />
              </label>
              <label>
                Class Time
                <input type="time" value={attendanceTime} onChange={e => setAttendanceTime(e.target.value)} />
              </label>
              <button className="faculty-attendance-add-btn" onClick={addSession}>
                <Plus size={15} /> Add Class
              </button>
            </div>
            {sessions.length === 0 ? (
              <div className="faculty-empty-state">
                <AlertCircle size={36} />
                <p>No attendance sessions yet. Add a class above to begin.</p>
              </div>
            ) : (
              <>
                <div className="faculty-attendance-table-wrap">
                  <table className="faculty-attendance-table">
                    <thead>
                      <tr>
                        <th style={{ width: '250px', minWidth: '250px' }}>Student</th>
                        <th style={{ width: '220px', minWidth: '220px' }}>USN / Department</th>
                        {sessions.map(session => (
                          <th key={session.id} className="faculty-attendance-session-head" style={{ width: '140px', minWidth: '140px' }}>
                            <div>
                              <strong>{formatDate(session.date)}</strong>
                              <span>{formatTime(session.time)}</span>
                            </div>
                            <button
                              type="button"
                              aria-label={`Remove class`}
                              onClick={() => removeSession(session.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </th>
                        ))}
                        <th style={{ width: '100%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.length === 0 ? (
                        <tr><td colSpan={3 + sessions.length} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>No students in this batch.</td></tr>
                      ) : (
                        students.map(student => (
                          <tr key={student.id}>
                            <td>
                              <strong>{student.name || `Student ${student.id}`}</strong>
                              <span><Mail size={12} /> {student.email || 'No email'}</span>
                            </td>
                            <td>{student.usn || 'No USN'} - {student.branch || student.department || student.degree || 'No department'}</td>
                            {sessions.map(session => (
                              <td key={session.id}>
                                <label className="faculty-attendance-check">
                                  <input
                                    type="checkbox"
                                    checked={getAttendanceStatus(id, session.id, student.id) === 'present'}
                                    onChange={(e) => setStatus(id, session.id, student.id, e.target.checked ? 'present' : 'absent')}
                                  />
                                  <span>{getAttendanceStatus(id, session.id, student.id) === 'present' ? 'Present' : 'Absent'}</span>
                                </label>
                              </td>
                            ))}
                            <td></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="faculty-attendance-save-row">
                  <button
                    type="button"
                    className="faculty-attendance-save-btn"
                    disabled={attendanceSaveStatus === 'saving'}
                    onClick={saveVisibleAttendance}
                  >
                    <Save size={16} /> {attendanceSaveStatus === 'saving' ? 'Saving...' : 'Save Attendance'}
                  </button>
                  <AttendanceExportControls assignmentId={id} batch={batch || 'All Batches'} />
                  {attendanceSaveStatus === 'saved' && <span className="faculty-attendance-save-message success">Attendance saved</span>}
                  {attendanceSaveStatus === 'error' && <span className="faculty-attendance-save-message error">Could not save attendance</span>}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── TEST MARKS TAB ── */}
        {detailSection === 'testMarks' && (
          <div className="faculty-panel">
            <div className="faculty-attendance-head">
              <div>
                <h3>Test Marks</h3>
                <p className="faculty-muted">{batch || 'All Batches'} · {students.length} students</p>
              </div>
              <label>
                Test Name
                <input
                  type="text"
                  placeholder="e.g. Midterm"
                  value={newTestName}
                  onChange={e => setNewTestName(e.target.value)}
                />
              </label>
              <label>
                Max Marks
                <input
                  type="number"
                  placeholder="100"
                  value={newMaxMarks}
                  onChange={e => setNewMaxMarks(e.target.value)}
                />
              </label>
              <button className="faculty-attendance-add-btn" onClick={addTest}>
                <Plus size={15} /> Add Test
              </button>
            </div>

            {tests.length === 0 ? (
              <div className="faculty-empty-state">
                <FileSpreadsheet size={36} />
                <p>No tests created yet. Add a test above to begin entering marks.</p>
              </div>
            ) : (
              <>
                <div className="faculty-attendance-table-wrap">
                  <table className="faculty-attendance-table">
                    <thead>
                      <tr>
                        <th style={{ width: '250px', minWidth: '250px' }}>Student</th>
                        <th style={{ width: '220px', minWidth: '220px' }}>USN / Department</th>
                        {tests.map(test => (
                          <th key={test.test_name} className="faculty-attendance-session-head" style={{ width: '140px', minWidth: '140px' }}>
                            <div>
                              <strong>{test.test_name.toUpperCase()}</strong>
                              <span>MAX: {test.max_marks}</span>
                            </div>
                            <button
                              type="button"
                              aria-label={`Remove test`}
                              onClick={() => removeTest(test.test_name)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </th>
                        ))}
                        <th style={{ width: '100%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.length === 0 ? (
                        <tr><td colSpan={3 + tests.length} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>No students in this batch.</td></tr>
                      ) : (
                        students.map(student => (
                          <tr key={student.id}>
                            <td>
                              <strong>{student.name || `Student ${student.id}`}</strong>
                              <span><Mail size={12} /> {student.email || 'No email'}</span>
                            </td>
                            <td>{student.usn || 'No USN'} - {student.branch || student.department || student.degree || 'No department'}</td>
                            {tests.map(test => (
                              <td key={test.test_name}>
                                <input
                                  type="number"
                                  min="0"
                                  max={test.max_marks}
                                  style={{ width: '80px', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)' }}
                                  value={getMarksDraft(id, test.test_name, student.id)}
                                  onChange={e => setMarksDraft(id, test.test_name, student.id, e.target.value)}
                                  placeholder="0"
                                />
                              </td>
                            ))}
                            <td></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="faculty-attendance-save-row">
                  <button
                    type="button"
                    className="faculty-attendance-save-btn"
                    disabled={testMarksSaveStatus === 'saving'}
                    onClick={saveVisibleTestMarks}
                  >
                    <Save size={16} /> {testMarksSaveStatus === 'saving' ? 'Saving...' : 'Save Marks'}
                  </button>
                  {testMarksSaveStatus === 'saved' && <span className="faculty-attendance-save-message success">Marks saved</span>}
                  {testMarksSaveStatus === 'error' && <span className="faculty-attendance-save-message error">Could not save marks</span>}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CATALOG / GRID VIEW
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0' }} className="faculty-dashboard">
      {/* Header */}
      <div className="faculty-page-heading">
        <h2>My Courses</h2>
        <p>Partner courses assigned to you by universities. Manage attendance and test marks.</p>
      </div>

      {/* Search + Refresh */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '0 16px', flex: 1 }}>
          <Search size={18} style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search courses by title or topic..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ border: 'none', background: 'transparent', padding: '14px 12px', outline: 'none', width: '100%', color: 'var(--text-main)' }}
          />
        </div>
        <button
          onClick={fetchCourses}
          disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '10px', background: 'var(--bg-card)', color: 'var(--text-main)', border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 600 }}
        >
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="uni-card" style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
          Loading your courses…
        </div>
      ) : filtered.length === 0 ? (
        <div className="uni-card empty-state" style={{ textAlign: 'center', padding: '60px' }}>
          <BookOpen size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '10px', fontSize: '20px' }}>No courses assigned yet</h3>
          <p style={{ color: 'var(--text-faint)' }}>
            {searchQuery ? 'No courses match your search.' : 'A university will assign you to a course batch. Check back soon.'}
          </p>
        </div>
      ) : (
        <div className="faculty-course-grid">
          {filtered.map(item => {
            const course = item.course || {};
            const moduleCount = Array.isArray(course.modules_data) ? course.modules_data.length : 0;
            const lessonCount = getLessonCount(course);
            return (
              <div
                key={item.id}
                className="faculty-course-card"
                onClick={() => { setSelectedAssignment(item); setDetailSection('attendance'); }}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { setSelectedAssignment(item); setDetailSection('attendance'); } }}
              >
                <div className="faculty-card-main">
                  <div className="faculty-card-kicker">
                    <span>{course.category || 'Curriculum'}</span>
                    <span>{item.batch || 'All Batches'}</span>
                  </div>
                  <h3>{course.title || 'Assigned Course'}</h3>
                  <div style={{ flex: 1, marginBottom: '12px' }}>
                    <p className="course-card-description" style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
                      {course.description || 'Course details will appear once the curriculum is available.'}
                    </p>
                    {course.description && course.description.length > 150 && (
                      <button
                        onClick={e => { e.stopPropagation(); setFullDescCourse(course); }}
                        style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: '0.85rem', fontWeight: 600, marginTop: '4px' }}
                      >
                        Read More
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                    <span><Layers size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{moduleCount} modules</span>
                    <span><Clock size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{lessonCount} lessons</span>
                    <span><Users size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{item.student_count || 0} students</span>
                  </div>
                  {item.university_name && (
                    <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>🏫 {item.university_name}</p>
                  )}
                </div>
                <div className="faculty-card-action">
                  <span>View Course &amp; Batch Roster</span>
                  <ArrowRight size={18} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Description Modal */}
      {fullDescCourse && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{fullDescCourse.title}</h3>
              <button className="modal-close" onClick={() => setFullDescCourse(null)}>×</button>
            </div>
            <div className="modal-content custom-scrollbar" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <p style={{ color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {fullDescCourse.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopLMSSection;
